import {Button, Stack, TextField, Checkbox, FormControlLabel, useMediaQuery, Tab, Tabs, Typography} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { FormBody } from "avrora";
import { ConfigurationContext, ConfigurationProvider } from "../../App";
import { Check } from "@mui/icons-material";

export const Config = () => {
    const [tab, setTab] = useState('interface');

    return (
        <ConfigurationProvider>
            <Stack spacing={2}>
                <Tabs
                    value={tab}
                    onChange={(_e, value) => {
                        setTab(value);
                    }}
                >
                    <Tab value="interface" label="Настройки интерфейса" />
                    <Tab value="password" label="Настройки парольных политик" />
                    <Tab value="privacy" label="Политика обработки персональных данных" />
                </Tabs>

                {tab === "interface" ? (
                    <Page />
                ) : tab === "password" ? (
                    <PasswordPolicyPage />
                ) : (
                    <PrivacyPolicyPage />
                )}
            </Stack>
        </ConfigurationProvider>
    );
};

export const Page = () => {
    const [configuration, setConfiguration] = useContext(ConfigurationContext);
    const excludedProperties = [
        'textOfPersonalInfoProcessingAgreement', 
        'linkToPersonalInfoProcessingPolicy', 
        'requirePersonalInfoProcessingConfirmation',
        'maxLength',
        'minLength',
        'maxNumberOfFailedLogins',
        'passwordMaxLifetime',
        'requireLowercaseSymbols',
        'requireSpecialChars',
        'requireUppercaseSymbols',
        'restrictRepeatingPasswords'
    ];
    
    const getFilteredConfiguration = (config: any) => {
        return Object.keys(config)
            .filter(item => !excludedProperties.includes(item))
            .reduce<any>((coll, item) => [...coll, { propertyName: item, propertyValue: config[item] }], []);
    };

    const [value, setValue] = useState(getFilteredConfiguration(configuration));
    const isMobile = useMediaQuery("(max-width:499px)");

    useEffect(() => {
        setValue(getFilteredConfiguration(configuration));
    }, [configuration]);

    const getPropertyLabel = (propertyName: string) => {
        if (propertyName === 'defaultSupportEmail') {
            return 'Ящик поддержки';
        }
        if (propertyName === 'bottomPageInfo') {
            return 'Текст футера';
        }
        return propertyName;
    };

    const disabled = useMemo(
        () =>
            JSON.stringify(getFilteredConfiguration(configuration)) === JSON.stringify(value),
        [value, configuration]
    );

    return (
        <Stack
            maxWidth={isMobile ? "100%" : "1024px"}
            minWidth={isMobile ? "auto" : "1024px"}
            padding={isMobile ? "1rem" : "3rem"}
            spacing={2}
        >
            <Typography variant="caption" color="text.secondary">
                Не указывайте секретные данные, данные доступны без аутентификации
            </Typography>
            {value?.map((i: any, j: number) => {
                const isBoolean = typeof i?.propertyValue === 'boolean';
                const propertyLabel = getPropertyLabel(i?.propertyName);
                
                return (
                    <Stack key={j} spacing={1}>
                        {isBoolean ? (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={i?.propertyValue === true}
                                        onChange={(e) =>
                                            setValue((old: FormBody) =>
                                                old.map((ii: any, jj: number) => (jj === j ? { ...ii, propertyValue: e.target.checked } : ii))
                                            )
                                        }
                                    />
                                }
                                label={propertyLabel}
                            />
                        ) : (
                            <TextField
                                size="small"
                                label={propertyLabel}
                                fullWidth
                                multiline={i?.propertyName === 'bottomPageInfo'}
                                rows={i?.propertyName === 'bottomPageInfo' ? 4 : undefined}
                                slotProps={{
                                    input: {
                                        autoComplete: "new-password",
                                    },
                                }}
                                value={i?.propertyValue}
                                onChange={(e) =>
                                    setValue((old: FormBody) =>
                                        old.map((ii: any, jj: number) => (jj === j ? { ...ii, propertyValue: e.target.value } : ii))
                                    )
                                }
                            />
                        )}
                        {i?.propertyName === 'bottomPageInfo' && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                будет показан на всех пользовательских страницах
                            </Typography>
                        )}
                    </Stack>
                );
            })}

            <Stack direction="row" sx={{ gap: isMobile ? 1 : 2 }} flexWrap="wrap" gap={1}>
                <Button
                    disabled={disabled}
                    onClick={() => {
                        const updatedFields = value.reduce((coll: any, item: any) => ({ ...coll, [item.propertyName]: item.propertyValue }), {});
                        setConfiguration({
                            ...configuration,
                            ...updatedFields
                        });
                    }}
                    startIcon={<Check />}
                    size="medium"
                    fullWidth={isMobile}
                >
                    Сохранить
                </Button>
            </Stack>
        </Stack>
    );
};

export const PasswordPolicyPage = () => {
    const [configuration, setConfiguration] = useContext(ConfigurationContext);
    const isMobile = useMediaQuery("(max-width:499px)");

    const [minLength, setMinLength] = useState(configuration.minLength ?? '');
    const [maxLength, setMaxLength] = useState(configuration.maxLength ?? '50');
    const [requireSpecialChars, setRequireSpecialChars] = useState(configuration.requireSpecialChars ?? false);
    const [requireLowercaseSymbols, setRequireLowercaseSymbols] = useState(configuration.requireLowercaseSymbols ?? false);
    const [requireUppercaseSymbols, setRequireUppercaseSymbols] = useState(configuration.requireUppercaseSymbols ?? false);
    const [passwordMaxLifetime, setPasswordMaxLifetime] = useState(configuration.passwordMaxLifetime ?? '');
    const [restrictRepeatingPasswords, setRestrictRepeatingPasswords] = useState(configuration.restrictRepeatingPasswords ?? false);
    const [maxNumberOfFailedLogins, setMaxNumberOfFailedLogins] = useState(configuration.maxNumberOfFailedLogins ?? '');

    useEffect(() => {
        setMinLength(configuration.minLength ?? '');
        setMaxLength(configuration.maxLength ?? '50');
        setRequireSpecialChars(configuration.requireSpecialChars ?? false);
        setRequireLowercaseSymbols(configuration.requireLowercaseSymbols ?? false);
        setRequireUppercaseSymbols(configuration.requireUppercaseSymbols ?? false);
        setPasswordMaxLifetime(configuration.passwordMaxLifetime ?? '');
        setRestrictRepeatingPasswords(configuration.restrictRepeatingPasswords ?? false);
        setMaxNumberOfFailedLogins(configuration.maxNumberOfFailedLogins ?? '');
    }, [configuration]);

    const disabled = useMemo(
        () => {
            const configMinLength = configuration.minLength ?? '';
            const configMaxLength = configuration.maxLength ?? '50';
            const configRequireSpecialChars = configuration.requireSpecialChars ?? false;
            const configRequireLowercaseSymbols = configuration.requireLowercaseSymbols ?? false;
            const configRequireUppercaseSymbols = configuration.requireUppercaseSymbols ?? false;
            const configPasswordMaxLifetime = configuration.passwordMaxLifetime ?? '';
            const configRestrictRepeatingPasswords = configuration.restrictRepeatingPasswords ?? false;
            const configMaxNumberOfFailedLogins = configuration.maxNumberOfFailedLogins ?? '';

            return String(configMinLength) === String(minLength) &&
                String(configMaxLength) === String(maxLength) &&
                configRequireSpecialChars === requireSpecialChars &&
                configRequireLowercaseSymbols === requireLowercaseSymbols &&
                configRequireUppercaseSymbols === requireUppercaseSymbols &&
                String(configPasswordMaxLifetime) === String(passwordMaxLifetime) &&
                configRestrictRepeatingPasswords === restrictRepeatingPasswords &&
                String(configMaxNumberOfFailedLogins) === String(maxNumberOfFailedLogins);
        },
        [minLength, maxLength, requireSpecialChars, requireLowercaseSymbols, requireUppercaseSymbols, passwordMaxLifetime, restrictRepeatingPasswords, maxNumberOfFailedLogins, configuration]
    );

    const handleSave = () => {
        setConfiguration({
            ...configuration,
            minLength,
            maxLength,
            requireSpecialChars,
            requireLowercaseSymbols,
            requireUppercaseSymbols,
            passwordMaxLifetime,
            restrictRepeatingPasswords,
            maxNumberOfFailedLogins,
        });
    };

    return (
        <Stack
            maxWidth={isMobile ? "100%" : "1024px"}
            minWidth={isMobile ? "auto" : "1024px"}
            padding={isMobile ? "1rem" : "3rem"}
            spacing={3}
        >
            <Typography variant="h6">Настройки требований к паролю</Typography>

            <Stack spacing={2}>
                <TextField
                    size="small"
                    label="Минимальная длина"
                    type="number"
                    fullWidth
                    value={minLength}
                    onChange={(e) => setMinLength(e.target.value)}
                    slotProps={{
                        input: {
                            autoComplete: "new-password",
                        },
                    }}
                />

                <TextField
                    size="small"
                    label="Максимальная длина"
                    type="number"
                    fullWidth
                    value={maxLength}
                    onChange={(e) => setMaxLength(e.target.value)}
                    slotProps={{
                        input: {
                            autoComplete: "new-password",
                        },
                    }}
                />

                <Stack spacing={1}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={requireSpecialChars}
                                onChange={(e) => setRequireSpecialChars(e.target.checked)}
                            />
                        }
                        label="Требовать наличие спецсимволов (хотя бы один из списка)"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                        @ % $ # * & ^ ( ) {'{ }'} [ ] - {'< >'} / | _
                    </Typography>
                </Stack>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={requireLowercaseSymbols && requireUppercaseSymbols}
                            onChange={(e) => {
                                setRequireLowercaseSymbols(e.target.checked);
                                setRequireUppercaseSymbols(e.target.checked);
                            }}
                        />
                    }
                    label="Требовать наличие строчных и прописных букв"
                />
            </Stack>

            <Typography variant="h6" sx={{ mt: 2 }}>Настройки жизненного цикла паролей</Typography>

            <Stack spacing={2}>
                <TextField
                    size="small"
                    label="Требовать смену пароля через Х дней"
                    type="number"
                    fullWidth
                    value={passwordMaxLifetime}
                    onChange={(e) => setPasswordMaxLifetime(e.target.value)}
                    slotProps={{
                        input: {
                            autoComplete: "new-password",
                        },
                    }}
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={restrictRepeatingPasswords}
                            onChange={(e) => setRestrictRepeatingPasswords(e.target.checked)}
                        />
                    }
                    label="Запретить повторяющиеся пароли (чтобы нельзя было два раза подряд установить один и тот же пароль)"
                />

                <TextField
                    size="small"
                    label="Количество неуспешных попыток входа в систему, после которого пользователь приостанавливается (не блокируется)"
                    type="number"
                    fullWidth
                    value={maxNumberOfFailedLogins}
                    onChange={(e) => setMaxNumberOfFailedLogins(e.target.value)}
                    slotProps={{
                        input: {
                            autoComplete: "new-password",
                        },
                    }}
                />
            </Stack>

            <Stack direction="row" sx={{ gap: isMobile ? 1 : 2 }} flexWrap="wrap" gap={1}>
                <Button
                    disabled={disabled}
                    onClick={handleSave}
                    startIcon={<Check />}
                    size="medium"
                    fullWidth={isMobile}
                >
                    Сохранить
                </Button>
            </Stack>
        </Stack>
    );
};

export const PrivacyPolicyPage = () => {
    const [configuration, setConfiguration] = useContext(ConfigurationContext);
    const isMobile = useMediaQuery("(max-width:499px)");

    const [requirePersonalInfoProcessingConfirmation, setRequirePersonalInfoProcessingConfirmation] = useState(
        configuration.requirePersonalInfoProcessingConfirmation ?? false
    );
    const [linkToPersonalInfoProcessingPolicy, setLinkToPersonalInfoProcessingPolicy] = useState(
        configuration.linkToPersonalInfoProcessingPolicy ?? ''
    );
    const [textOfPersonalInfoProcessingAgreement, setTextOfPersonalInfoProcessingAgreement] = useState(
        configuration.textOfPersonalInfoProcessingAgreement ?? ''
    );

    useEffect(() => {
        setRequirePersonalInfoProcessingConfirmation(configuration.requirePersonalInfoProcessingConfirmation ?? false);
        setLinkToPersonalInfoProcessingPolicy(configuration.linkToPersonalInfoProcessingPolicy ?? '');
        setTextOfPersonalInfoProcessingAgreement(configuration.textOfPersonalInfoProcessingAgreement ?? '');
    }, [configuration]);

    const disabled = useMemo(
        () =>
            configuration.requirePersonalInfoProcessingConfirmation === requirePersonalInfoProcessingConfirmation &&
            configuration.linkToPersonalInfoProcessingPolicy === linkToPersonalInfoProcessingPolicy &&
            configuration.textOfPersonalInfoProcessingAgreement === textOfPersonalInfoProcessingAgreement,
        [requirePersonalInfoProcessingConfirmation, linkToPersonalInfoProcessingPolicy, textOfPersonalInfoProcessingAgreement, configuration]
    );

    const handleSave = () => {
        setConfiguration({
            ...configuration,
            requirePersonalInfoProcessingConfirmation,
            linkToPersonalInfoProcessingPolicy,
            textOfPersonalInfoProcessingAgreement,
        });
    };

    return (
        <Stack
            maxWidth={isMobile ? "100%" : "1024px"}
            minWidth={isMobile ? "auto" : "1024px"}
            padding={isMobile ? "1rem" : "3rem"}
            spacing={2}
        >
            <FormControlLabel
                control={
                    <Checkbox
                        checked={requirePersonalInfoProcessingConfirmation}
                        onChange={(e) => setRequirePersonalInfoProcessingConfirmation(e.target.checked)}
                    />
                }
                label="Требовать подтверждение обработки персональных данных"
            />

            <TextField
                size="small"
                label="Ссылка на политику обработки персональных данных"
                fullWidth
                value={linkToPersonalInfoProcessingPolicy}
                onChange={(e) => setLinkToPersonalInfoProcessingPolicy(e.target.value)}
                slotProps={{
                    input: {
                        autoComplete: "new-password",
                    },
                }}
            />

            <TextField
                size="small"
                label="Текст соглашения об обработке персональных данных"
                fullWidth
                multiline
                rows={4}
                value={textOfPersonalInfoProcessingAgreement}
                onChange={(e) => setTextOfPersonalInfoProcessingAgreement(e.target.value)}
                slotProps={{
                    input: {
                        autoComplete: "new-password",
                    },
                }}
            />

            <Stack direction="row" sx={{ gap: isMobile ? 1 : 2 }} flexWrap="wrap" gap={1}>
                <Button
                    disabled={disabled}
                    onClick={handleSave}
                    startIcon={<Check />}
                    size="medium"
                    fullWidth={isMobile}
                >
                    Сохранить
                </Button>
            </Stack>
        </Stack>
    );
};
